import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import Dialog from '../components/Dialog';
import { DRButton } from '../components/DRControls';
import Header from '../components/DRHeader';
import { HorizontalButtonPairView } from '../components/DRViewUtils';
import RestrictedCheckbox from '../components/RestrictedCheckbox';
import WebPicker from '../components/WebPicker';
import { cancelColor, confirmColor, confirmBorderColor } from '../constants/Styles';
import { ModalParams } from '../types/commonInterfaces';
import { Dropoff } from '../types/taskInterfaces';
import { getConfig } from '../utils/configUtils';
import { isMobile, areSame, clearFetch, getShared} from '../vodjs/Utils';
import { getUnitName, h2r, getRobotId } from '../vodjs/lib';
import {getCurStepStatus, onBotTaskCancel} from '../vodjs/PollMgr';
import { elasticLogEvent, elasticLogError } from '../vodjs/ElasticLog';

import './RequestScreen.scss';

const mobile = isMobile();

const PLEASE_SELECT = 'Choose a location';

interface RequestScreenProps {
  disableStartGoalSelection: boolean;
  endGoals: string[];
  defaultStartGoal: string;
  closeModal: (params?: ModalParams) => void;
  clickRequest: () => void;
  dropoffs: Dropoff[];
  setDropoffs: (params: Dropoff[]) => void;
  isRestricted: boolean;
  setRestricted: (value: boolean) => void;
  isCodeRequired: boolean;
  setPinRequired: (value: boolean) => void;
  showRestricted: boolean;
  noKioskText: string;
  getOpenHoursInfo: (stationCode:string) => any;
  onBotEditCtx?: any;
  autoPrioConfig?:any;
  drawersAllowStopChanges?:boolean;
  noteOnChange?: (value: string) => void;
  isTest: boolean;
}

export default function RequestScreen(props: RequestScreenProps) {
  const {
    clickRequest,
    closeModal,
    defaultStartGoal,
    dropoffs,
    endGoals,
    isRestricted,
    setDropoffs,
    setRestricted,
    showRestricted,
    noKioskText,
    isCodeRequired,
    setPinRequired,
    getOpenHoursInfo,
    onBotEditCtx,
    autoPrioConfig,
    drawersAllowStopChanges,
    noteOnChange,
    isTest
  } = props;

  const [visibleAlert, setVisibleAlert] = useState<any>(undefined);
  const [cleared, setCleared] = useState<any>([]);

  const expandedEndgoals = [PLEASE_SELECT, ...endGoals];


  useEffect(() => {
    if(onBotEditCtx)
      return; //during an onbot edit, user should dictate initial dropoff

    if (!dropoffs.length) {
      setDropoffs([
        {
          id: 0,
          startGoal: getUnitName(),
          endGoal: PLEASE_SELECT,
          note: '',
        },
      ]);
    }
  }, [dropoffs]);   //was [dropoffs, setDropoffs]


  useEffect(() => {
    if(cleared.length)
      handleClickRequest();
  }, [cleared]);

  const invalidDropoffs = () => dropoffs.findIndex((d) => d.endGoal === PLEASE_SELECT) >= 0;

  const onBotEditHasChange = () => {
    return onBotEditCtx && (!areSame(onBotEditCtx.origBatch.map(e => e.endGoal), onBotEditCtx.editedBatch.map(e => e.endGoal)) || (onBotEditCtx.restricted != onBotEditCtx.origRestricted));
  }

  const handleClickRequest = async () => {

    if (invalidDropoffs()) {
      setVisibleAlert({msg:'You must select a destination.'});
      return;
    }

    for(let dropoff of dropoffs) {
      const code = h2r(dropoff.endGoal);
      if(cleared.indexOf(code) < 0) {
        let openHoursInfo = getOpenHoursInfo(code);
        if(openHoursInfo) {
          setVisibleAlert({msg:openHoursInfo.msg, userChoices:openHoursInfo.userChoices, yesCB:() => {
            setCleared((prevCleared:any) => [...prevCleared, code]);
            setVisibleAlert(undefined);
          }});
          return;
        }
      }
    }

    if(onBotEditCtx) {
      if(!onBotEditHasChange()) {
        closeModal();  //no change
      }
      else if(!drawersAllowStopChanges) {
        setVisibleAlert({msg:`Changes to stops cannot be made once a drawer has been opened.`});
      }
      else {
        const curStepStatus = getCurStepStatus();
        const oldLoadTaskUuid = curStepStatus?.task_uuid;

        setVisibleAlert({msg:`Attempting changes...`, buttonLess:true});
        // await sleep(2000); //simulate wait
        let errStr = await attemptStationChange();
        if(errStr) {
          elasticLogError({ what: 'stationChangeErr', msg:`error attempting station changes: ${errStr}`, errStr });
          setVisibleAlert({msg:errStr});
        }
        else {
          //successfully enacted backend changes; wait for the bot to notice task canceled
          setVisibleAlert({msg:`One moment...`, buttonLess:true});

          if(autoPrioConfig?.loadEditDoubleTap) {
            //attempt local kill of current task
            if(oldLoadTaskUuid) {
              onBotTaskCancel(oldLoadTaskUuid); //async, best-effort
            }
            else {
              elasticLogEvent({what:'noTaskToKill', msg:'after changed stops, curStepStatus has no task_uuid, so not attempting local kill', curStepStatus});
            }
          }

          if(autoPrioConfig?.allowInterstitials) {
            const expiresTicks = Date.now() + 1000 * 60;  //arbitrary; hopefully enough time for next task to get picked up
            getShared().interstitial = () => {

              if(Date.now() > expiresTicks) {
                getShared().interstitial = undefined;
                return;
              }

              let curStepStatus = getCurStepStatus();
              let updatedTaskUuid = getCurStepStatus()?.task_uuid;
              if(updatedTaskUuid && updatedTaskUuid != oldLoadTaskUuid && curStepStatus.step_type == 'load_tube') {
                getShared().interstitial = undefined;
                return;
              }

              return `Moxi is reading the new tasks...`;
            }
          }
        }
      }
    }
    else {
      clickRequest();
    }
  };



  //on failure, returns string with error; on success returns nothing
  async function attemptStationChange() {
    let errStr, options:any;

    //Do prelim check that we appear able to reach the backend
    const curStepStatus = getCurStepStatus();
    const oldLoadTaskUuid = curStepStatus?.task_uuid;

    if(autoPrioConfig?.changeStopsNetTimeout) {
      let timeoutTicks = autoPrioConfig.changeStopsNetTimeout * 1000;

      options = { method: 'PUT', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }};
      options.body = JSON.stringify({stationChangePrecheck:{robotId:getRobotId(), oldLoadTaskUuid}});
      await clearFetch(new URL('checkVersion', getConfig().uiSimServer), options, {
        bad: ({logStr}) => {debugger; errStr = logStr;}
      }, timeoutTicks);
      if(errStr)
          return `network check: ` + errStr;

      elasticLogEvent({what:'stationChgNetPass', msg:`Passed network check for station change`, oldLoadTaskUuid});
    }

    options = { method: 'PUT', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }};
    let {origBatch, editedBatch, restricted, codeRequired} = onBotEditCtx;
    options.body = JSON.stringify({robotId:getRobotId(), origBatch, editedBatch, restricted, code_required:codeRequired, test:isTest});
    await clearFetch(new URL('changeBatch', getConfig().uiSimServer), options, {
      bad: ({logStr, errJson}) => {
        if(errJson?.errCode == 'lowCharge') {
          errStr = `Moxi does not have enough charge to make these changes. Please use the 'Go back' button to continue with the existing tasks.`;
        }
        else {
          errStr = logStr;
          
          if(errStr.indexOf('Internal Server Error') >= 0)
            errStr = 'Something went wrong';
          else if(errStr.indexOf('url unreachable') >= 0)
            errStr = 'Could not reach server';
          else if(errStr.toLowerCase().indexOf('load failed') >= 0)
            errStr = 'Network call did not succeed';

          errStr = `Error attempting changes: ${errStr}.`;
        }
      }
    });
    return errStr;
  }



  const buttonSettings = [
    {
      id: 'cancel',
      phrase: onBotEditCtx ? 'GO BACK' : 'CANCEL',
      borderColor: cancelColor,
      fillColor: 'transparent',
      fontColor: cancelColor,
      action: () => closeModal(),
    },
    {
      id: 'request',
      phrase: onBotEditCtx ? 'CONFIRM' : 'REQUEST',
      fillColor: confirmColor,
      borderColor: confirmBorderColor,
      fontColor: 'white',
      disabled: onBotEditCtx ? !onBotEditHasChange() : invalidDropoffs(),
      action: () => handleClickRequest(),
    },
  ];

  const dropoffButton = () => (
    <DRButton
      key="secondary-button"
      phrase="ADD DROP-OFF"
      fillColor="transparent"
      borderColor="#416FE5"
      fontColor="#416FE5"
      action={clickAddDropoff}
      fontSize={24}
      height={44}
      width={230}
      customizeStyle={{ borderWidth: 3, whiteSpace: 'nowrap' }}
    />
  );

  const formatTitle = () => {
    if(onBotEditCtx)
      return <>Change delivery stops</>

    return (
      <>
        <span>Order a delivery</span>
        <strong>
          {' '}
          from
          <span className="content__title--highlight"> {defaultStartGoal} </span>
          to
        </strong>
      </>
    );
  };

  const clickAddDropoff = () => {
    let id = 0;

    if (dropoffs && dropoffs.length) {
      const currentDropoffs = dropoffs.slice().sort((a, b) => b.id - a.id);
      if (currentDropoffs[0] && typeof currentDropoffs[0].id === 'number') id = currentDropoffs[0].id + 1;
    }

    const newDropoff = {
      id,
      startGoal: dropoffs[dropoffs.length - 1].endGoal,
      endGoal: PLEASE_SELECT,
      note: '',
    };
    const currDropoffs = dropoffs.slice();
    setDropoffs([...currDropoffs, newDropoff]);
  };


  const clickRemoveDropoff = (index: number) => {
    const updatedDropoffs = dropoffs.slice();
    updatedDropoffs.splice(index, 1);

    for (let i = 1; i < updatedDropoffs.length; i++)
      updatedDropoffs[i].startGoal = updatedDropoffs[i - 1].endGoal;

    setDropoffs(updatedDropoffs);
  };


  const updateDropoff = (index: number, type: 'endGoal' | 'note', value: string) => {
    const updatedDropoffs = dropoffs.slice();
    const dropObj = updatedDropoffs[index];
    dropObj[type] = value;
    // updatedDropoffs[index] = dropObj; //this line is redundant with line above

    // On endGoal change, update startGoal for other locations
    if (type === 'endGoal' && updatedDropoffs.length > 1) {
      for (let i = 1; i < updatedDropoffs.length; i++) 
        updatedDropoffs[i].startGoal = updatedDropoffs[i - 1].endGoal;
    }

    setDropoffs(updatedDropoffs);

    //provision for single-task change stop edits
    if(index == 0 && type == 'note' && noteOnChange)
      noteOnChange(value);
  };

  const getDisabledValues = () => {
    const disabled = dropoffs.map((d) => d.endGoal);
    disabled.push(noKioskText);
    return disabled;
  };

  const getLocationInputClasses = (endGoal: string) =>
    `content__location-input${endGoal !== PLEASE_SELECT ? ' content__location-input--selected' : ''} content__location-font-${onBotEditCtx ? 'small' : 'big'}`;


  const canSelectMFA = autoPrioConfig?.pin_code_phase1;
  // If we are editing an MFA task, we cannot change the MFA or RD status
  const canToggleRestricted = (onBotEditCtx && isCodeRequired) ? false : autoPrioConfig?.allowLoadEditRestrictedToggle;

  return (
    <>
      <div className="request-screen">
        <div className="fade-bar"></div>
        <div className="request-screen__content">
          {!mobile && !onBotEditCtx && <Header showBackBt={false} />}
          <div className="content__scroll-container">
            <div className="content__scroll-wrapper">
              <div className="content__wrapper">
                {!mobile && <div className="content__title" style={{textAlign:onBotEditCtx ? 'center' : undefined}}>{formatTitle()}</div>}
                {dropoffs.map((dropoff, index) => (
                  <div className={`dropoff__wrapper-${dropoff.id}`} key={`dropoff-${dropoff.id}`}>
                    {index > 0 && (
                      <div className="and__button">
                        <strong>and</strong>
                        <button className="and__close" onClick={() => clickRemoveDropoff(index)}>
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      </div>
                    )}
                    <WebPicker
                      className={getLocationInputClasses(dropoff.endGoal)}
                      id={`end-goal-${index}`}
                      onValueChange={(itemValue: string) => updateDropoff(index, 'endGoal', itemValue)}
                      selectedValue={dropoff.endGoal}
                      values={expandedEndgoals}
                      disabledValues={getDisabledValues()}
                      openOnLoad={index > 0 ? dropoffs[index].endGoal === PLEASE_SELECT : false}
                      hiddenOptions={[PLEASE_SELECT]}
                      maxHeight={250}
                      activeStyle={{outline: '2px solid #416fe5'}}
                    />
                    <textarea
                      className="content__note-input"
                      style={{fontSize:onBotEditCtx ? 18 : undefined}}
                      onChange={(e) => updateDropoff(index, 'note', e.target.value)}
                      placeholder="Add a note"
                      value={dropoff.note}
                      rows={1}
                    />
                  </div>
                ))}
                {dropoffs.length < 3 && <div className="add-dropoff__wrapper">{dropoffButton()}</div>}
                {getConfig().secureDelivery && showRestricted && (
                  <>
                    <div className="restricted__wrapper">
                      <RestrictedCheckbox
                        isChecked={isRestricted}
                        handleChange={async (e) => setRestricted(e.target.checked)}
                        isDisabled={!canToggleRestricted}
                      />
                      {isRestricted && (
                        <div className="restricted__label">All deliveries will require restricted access</div>
                      )}
                    </div>
                    {canSelectMFA && (
                      <div className="mfa__wrapper">
                        <RestrictedCheckbox
                          isChecked={isCodeRequired}
                          label="Multi-factor Code"
                          handleChange={async (e) => setPinRequired(e.target.checked)}
                          isDisabled={onBotEditCtx || !isRestricted}
                        />
                        {isCodeRequired && (
                          <div className="restricted__label">All deliveries will also require a code{onBotEditCtx ? ` (can not be edited)` : ``}</div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="request-screen__footer">
                <HorizontalButtonPairView buttonSettings={buttonSettings} />
              </div>
            </div>
          </div>
        </div>
      </div>
      {!!visibleAlert && (
        <Dialog
          title={visibleAlert.msg}

          primaryButtonText={visibleAlert.userChoices?.no || (!visibleAlert.buttonLess ? "OKAY" : undefined)}
          primaryButtonWidth={!visibleAlert.userChoices ? 220 : undefined}
          clickPrimaryButton={() => setVisibleAlert(undefined)}

          secondaryButtonText={visibleAlert.userChoices?.yes}
          clickSecondaryButton={visibleAlert.yesCB || (() => setVisibleAlert(undefined))}
        />
      )}
    </>
  );
}
